import { Sun, DollarSign, Leaf, Shield, Home, Building2, LineChart, Battery, Award, Globe } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HomeHeader } from '../components/HomeHeader';

export function KeyBenefits() {
  return (
    <div className="min-h-screen bg-white">
      <HomeHeader />
      
      <main>
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Benefits of Solar Energy
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Discover how solar power can transform your energy consumption and benefit your property.
              </p>
            </div>
          </div>
        </div>

        {/* Financial Benefits */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Financial Benefits</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Maximize your return on investment with solar energy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Reduced Energy Bills</h3>
                <p className="text-gray-600">
                  Significantly lower your monthly electricity costs by generating your own power.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <LineChart className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Increased Property Value</h3>
                <p className="text-gray-600">
                  Solar installations can increase your property value by 4-6% on average.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Tax Incentives</h3>
                <p className="text-gray-600">
                  Take advantage of federal and state tax credits for solar installations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Environmental Benefits */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Environmental Impact</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Make a positive impact on the environment with clean energy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Leaf className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Reduced Carbon Footprint</h3>
                <p className="text-gray-600">
                  Lower your carbon emissions and help combat climate change.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Globe className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Sustainable Energy</h3>
                <p className="text-gray-600">
                  Harness clean, renewable energy from the sun.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Award className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Environmental Leadership</h3>
                <p className="text-gray-600">
                  Set an example for sustainable practices in your community.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Benefits */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Technical Advantages</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                State-of-the-art technology for optimal performance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-yellow-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Sun className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">High Efficiency</h3>
                <p className="text-gray-600">
                  Modern solar panels convert up to 23% of sunlight into electricity.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Battery className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Energy Storage</h3>
                <p className="text-gray-600">
                  Optional battery systems for 24/7 power availability.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Low Maintenance</h3>
                <p className="text-gray-600">
                  Minimal upkeep required with 25+ years of performance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Property Types */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Benefits for Every Property</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Tailored solutions for different property types.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <Home className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold ml-4">Residential Properties</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <Sun className="h-4 w-4 text-green-500 mr-2" />
                    Reduced monthly energy bills
                  </li>
                  <li className="flex items-center">
                    <Sun className="h-4 w-4 text-green-500 mr-2" />
                    Increased home value
                  </li>
                  <li className="flex items-center">
                    <Sun className="h-4 w-4 text-green-500 mr-2" />
                    Energy independence
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold ml-4">Commercial Properties</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <Sun className="h-4 w-4 text-green-500 mr-2" />
                    Reduced operating costs
                  </li>
                  <li className="flex items-center">
                    <Sun className="h-4 w-4 text-green-500 mr-2" />
                    Enhanced brand image
                  </li>
                  <li className="flex items-center">
                    <Sun className="h-4 w-4 text-green-500 mr-2" />
                    Tax advantages
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-blue-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Start Benefiting Today</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join thousands of satisfied customers who have made the switch to solar energy.
            </p>
            <button className="bg-white text-blue-600 py-3 px-8 rounded-lg hover:bg-blue-50 font-medium">
              Get Your Free Quote
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}